import { AudioStreamingState } from '../state/AudioPlayerStreamingStore';

import {
	appendBufferAsync,
	getBufferAheadSeconds,
	waitForBufferToDrain,
} from './AudioBufferEffects';

export type AudioPlayerStreamingAction<TResponse extends ReadableStream<Uint8Array<ArrayBuffer>>> =
	| { type: AudioStreamingState.LOADING }
	| { type: AudioStreamingState.ERROR; error: Error }
	| { type: AudioStreamingState.STREAM_STARTED; response: TResponse }
	| { type: AudioStreamingState.STREAM_DISPOSED };

export type AudioPlayerManagerConfig<
	TRequest,
	TResponse extends ReadableStream<Uint8Array<ArrayBuffer>>,
> = {
	fetchQuery: () => Promise<TResponse>;
	onAudioStreamingChange: (action: AudioPlayerStreamingAction<TResponse>) => void;
};

export class AudioPlayerStreamingManager<
	TRequest,
	TResponse extends ReadableStream<Uint8Array<ArrayBuffer>>,
> {
	private response: TResponse | null = null;
	private audioElement: HTMLAudioElement | null = null;
	private mediaSource: MediaSource | null = null;
	private isReadyToConsumeStream: boolean = false;

	constructor(
		private config: AudioPlayerManagerConfig<TRequest, TResponse>,
		private readonly audioMimeType: string = 'audio/mpeg',
		private readonly maxBufferAheadSeconds: number = 360,
		private readonly resumeBufferAheadSeconds: number = 180,
	) {}

	async fetchAudio() {
		this.config.onAudioStreamingChange({ type: AudioStreamingState.LOADING });
		try {
			this.response = await this.config.fetchQuery();
			this.config.onAudioStreamingChange({
				type: AudioStreamingState.STREAM_STARTED,
				response: this.response,
			});
		} catch (error) {
			this.config.onAudioStreamingChange({
				type: AudioStreamingState.ERROR,
				error: error as Error,
			});
		}
	}

	streamAudioToMediaSource(audioElement: HTMLAudioElement | null) {
		if (!audioElement || !this.response) {
			return;
		}

		this.mediaSource = new MediaSource();
		this.audioElement = audioElement;
		this.isReadyToConsumeStream = true;

		const url = URL.createObjectURL(this.mediaSource);
		this.audioElement.src = url;
		this.audioElement.load();

		this.mediaSource.addEventListener('sourceopen', () => this.onMediaSourceOpen(url), {
			once: true,
		});
	}

	disposeStream() {
		this.isReadyToConsumeStream = false;
		if (this.mediaSource?.readyState === 'open') {
			try {
				this.mediaSource.endOfStream();
			} catch (e) {
				// Errors during cleanup can be safely ignored.
			}
		}
		this.audioElement = null;
		this.response = null;
		this.config.onAudioStreamingChange({ type: AudioStreamingState.STREAM_DISPOSED });
	}

	private onMediaSourceOpen = async (url: string) => {
		if (!this.mediaSource || !this.response || !this.audioElement) {
			return;
		}

		try {
			URL.revokeObjectURL(url);
			const sourceBuffer = this.mediaSource.addSourceBuffer(this.audioMimeType);
			sourceBuffer.mode = 'sequence';
			const reader = this.response.getReader();

			while (this.isReadyToConsumeStream) {
				// Backpressure: if bufferAhead is at or above the cap, wait for it to drain
				const bufferAheadSeconds = getBufferAheadSeconds(this.audioElement);
				if (bufferAheadSeconds >= this.maxBufferAheadSeconds) {
					await waitForBufferToDrain(
						this.audioElement,
						this.isReadyToConsumeStream,
						this.resumeBufferAheadSeconds,
					);
					if (!this.isReadyToConsumeStream) {
						break;
					}
				}
				const { done, value } = await reader.read();
				if (done) {
					break;
				}

				if (!this.isReadyToConsumeStream) {
					break;
				}
				await appendBufferAsync(sourceBuffer, value);
			}

			if (this.isReadyToConsumeStream) {
				this.disposeStream();
			}
		} catch (e) {
			this.config.onAudioStreamingChange({ type: AudioStreamingState.ERROR, error: e as Error });
		}
	};
}
